import { TOOL_CALL_STATUS } from '@kentik/ui-shared/kaizen/constants';

/**
 * Tools that surface specific error messages to the user rather than the
 * generic "something went wrong" fallback. Add tool names here when a tool
 * produces actionable, user-facing error messages (e.g. RBAC denials).
 */
const DEVICE_SPECIFIC_ERROR_TOOLS = ['device_config', 'device_command'];

/**
 * Permission strings that indicate a device-specific RBAC error at the turn level.
 * These appear in turn.error_message when the tool fails before a tool call is recorded.
 */
const DEVICE_PERMISSION_PATTERNS = [
  'device.command::execute',
  'device.config::read',
  'You do not have permission for:'
];

/**
 * Returns true if the given tool name should show its actual error message.
 * @param {string} toolName
 * @returns {boolean}
 */
export const isDeviceSpecificErrorTool = (toolName) => DEVICE_SPECIFIC_ERROR_TOOLS.includes(toolName);

/**
 * Returns true if any tool call in the array belongs to a device-specific error tool.
 * @param {Array} toolCalls
 * @returns {boolean}
 */
export const stepHasDeviceToolCall = (toolCalls) =>
  toolCalls?.some((tc) => isDeviceSpecificErrorTool(tc.tool_name)) ?? false;

/**
 * Returns true if the turn error should show the actual error message.
 * Checks both step tool calls and the error message text itself (for cases
 * where the RBAC error is thrown before a tool call is recorded in steps).
 * @param {object} turn
 * @returns {boolean}
 */
export const isDeviceSpecificTurnError = (turn) => {
  if (!turn?.error_message) {
    return false;
  }
  if (turn.steps?.some((s) => stepHasDeviceToolCall(s.toolCalls))) {
    return true;
  }
  return DEVICE_PERMISSION_PATTERNS.some((pattern) => turn.error_message.includes(pattern));
};

// Both persist the raw error in error_message, and copy it into processed_result too.
const ERRORED_TOOL_CALL_STATUSES = [TOOL_CALL_STATUS.FAILED, TOOL_CALL_STATUS.TIMED_OUT];

/**
 * Returns true if the tool call failed or timed out.
 * @param {object} toolCall
 * @returns {boolean}
 */
export const isErroredToolCall = (toolCall) => ERRORED_TOOL_CALL_STATUSES.includes(toolCall?.status);
